"use client";

import React, { useState, useRef } from "react";
import Image from "next/image";
import SmoothImage from "@/components/SmoothImage";
import { renderTextWithBreaks } from "@/utils/stripHTML";
import { cn } from "@/utils/cn";
import GalleryModal, {
  MediaItem,
} from "@/components/Layouts/components/GalleryModal";
import VideoModal from "@/components/Layouts/components/VideoModal";

export interface GalleryCardItem {
  id: string;
  tag: string;
  title: string;
  type: "image" | "video";
  thumb: string;
  media: MediaItem[];
}

export interface GallerySectionData {
  tag?: string;
  title?: string;
  description?: string;
  ctaLink?: {
    label: string;
    url?: string;
  };
  items?: GalleryCardItem[];
}

interface GallerySectionProps {
  data: GallerySectionData;
  onCtaClick?: () => void;
  onCardClick?: (cardId: string) => void;
}

export default function GallerySection({
  data,
  onCtaClick,
  onCardClick,
}: GallerySectionProps) {
  if (!data || !data.items || data.items.length === 0) return null;

  const {
    tag = "THƯ VIỆN DRAGON HOME",
    title = "Những lát cắt của một đời sống kết nối",
    ctaLink = {
      label: "XEM TOÀN BỘ THƯ VIỆN",
      url: "#",
    },
    items = [],
  } = data;

  const [activeFilter, setActiveFilter] = useState<"all" | "image" | "video">(
    "all",
  );

  // Gallery Modal State (for images)
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMedia, setModalMedia] = useState<MediaItem[]>([]);
  const [modalIndex, setModalIndex] = useState(0);

  // Dedicated Video Modal State (strictly for video cards)
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoTitle, setVideoTitle] = useState("");

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const filteredItems = items.filter((item) => {
    if (activeFilter === "image") return item.type === "image";
    if (activeFilter === "video") return item.type === "video";
    return true;
  });

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -360, behavior: "smooth" });
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 360, behavior: "smooth" });
    }
  };

  const handleOpenCardGallery = (card: GalleryCardItem) => {
    if (card.type === "video") {
      const videoMedia = card.media?.find((m) => m.type === "video");
      const urlToPlay = videoMedia?.src || card.thumb;

      setVideoUrl(urlToPlay);
      setVideoTitle(card.title || card.tag || "");
      setVideoModalOpen(true);
    } else {
      const mediaList =
        card.media && card.media.length > 0
          ? card.media
          : [
              {
                type: card.type,
                src: card.thumb,
                thumb: card.thumb,
                title: card.title,
              },
            ];

      setModalMedia(mediaList);
      setModalIndex(0);
      setModalOpen(true);
    }

    if (onCardClick) {
      onCardClick(card.id);
    }
  };

  return (
    <section
      id="thu-vien"
      className="relative w-full h-auto lg:h-screen lg:min-h-screen lg:max-h-screen flex flex-col justify-center overflow-hidden text-[#193B2F] select-none px-6 sm:px-10 lg:px-14 xl:px-16 pt-12 sm:pt-16 lg:pt-20 pb-6 sm:pb-8 lg:pb-8"
    >
      {/* BACKGROUND TEXTURE OVERLAY */}
      <div className="absolute inset-0 z-0 pointer-events-none mix-blend-multiply">
        <Image
          src="/images/background_2.png"
          alt="texture"
          fill
          sizes="100vw"
          priority
          className="object-cover object-center"
        />
      </div>

      {/* CENTERED CONTAINER ON DESKTOP */}
      <div className="relative z-10 w-full max-w-7xl mx-auto my-0 lg:my-auto flex flex-col justify-center gap-4 sm:gap-6 lg:gap-8">
        {/* ================= 1. HEADER ROW ================= */}
        <div className="w-full flex flex-col lg:flex-row justify-between items-start lg:items-end gap-4 lg:gap-6 flex-shrink-0">
          {/* LEFT: TAG & MAIN TITLE */}
          <div className="w-full lg:w-[55%] space-y-1.5">
            <div className="flex items-center gap-3">
              <span className="w-7 h-[1px] bg-[#8A724C]" />
              <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-[#8A724C] font-sans">
                {tag}
              </span>
            </div>

            <h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-lora font-normal text-[#193B2F] tracking-tight title-leading"
              style={{ lineHeight: 1.15 }}
            >
              {renderTextWithBreaks(title)}
            </h2>
          </div>

          {/* RIGHT: 3 FILTER TABS, 2 NAV BUTTONS & CTA LINK */}
          <div className="w-full lg:w-[42%] flex flex-col items-start lg:items-end space-y-3 lg:pb-0.5">
            <div className="flex flex-wrap items-center gap-3">
              {/* 3 FILTER BUTTONS TABS */}
              <div className="flex items-center gap-1 p-1 bg-[#EAE5DB] border border-[#193B2F]/10 rounded-none select-none">
                {(
                  [
                    { id: "all", label: "TẤT CẢ" },
                    { id: "image", label: "HÌNH ẢNH" },
                    { id: "video", label: "VIDEO" },
                  ] as const
                ).map((tab) => {
                  const isActive = activeFilter === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveFilter(tab.id)}
                      className={cn(
                        "px-3.5 sm:px-4 py-1.5 text-[10px] sm:text-xs font-sans font-semibold tracking-wider transition-colors cursor-pointer uppercase border",
                        isActive
                          ? "bg-[#E5D4B2] text-[#193B2F] border-[#C9AB81] font-bold shadow-sm"
                          : "bg-[#FAF8F5] text-[#193B2F]/60 border-transparent hover:text-[#193B2F] hover:bg-[#F2ECE1]",
                      )}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* 2 LIGHT BUTTONS TO MOVE LEFT / RIGHT */}
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleScrollLeft}
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-full border border-[#193B2F]/20 text-[#193B2F] hover:bg-[#E5D4B2] hover:border-[#193B2F]/40 transition-colors flex items-center justify-center cursor-pointer focus:outline-none shadow-sm"
                  aria-label="Trượt sang trái"
                >
                  <svg
                    className="w-4 h-4 fill-current rotate-180"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M5 12h14M12 5l7 7-7 7"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={handleScrollRight}
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-full border border-[#193B2F]/20 text-[#193B2F] hover:bg-[#E5D4B2] hover:border-[#193B2F]/40 transition-colors flex items-center justify-center cursor-pointer focus:outline-none shadow-sm"
                  aria-label="Trượt sang phải"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path
                      d="M5 12h14M12 5l7 7-7 7"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* CTA LINK */}
            {ctaLink && ctaLink.label && (
              <a
                href={ctaLink.url || "#"}
                onClick={(e) => {
                  if (onCtaClick) {
                    e.preventDefault();
                    onCtaClick();
                  }
                }}
                className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-sans font-bold tracking-[0.16em] uppercase text-[#193B2F]/80 hover:text-[#193B2F] transition-colors group"
              >
                <span>{ctaLink.label}</span>
                <span className="text-xs transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                  ↗
                </span>
              </a>
            )}
          </div>
        </div>

        {/* ================= 2. CARDS SLIDER CONTAINER ================= */}
        <div className="w-full relative overflow-hidden my-1">
          <div
            ref={scrollContainerRef}
            className="flex gap-4 lg:gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth py-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          >
            {filteredItems.map((item, idx) => (
              <div
                key={item.id ? `${item.id}-${idx}` : `gallery-item-${idx}`}
                onClick={() => handleOpenCardGallery(item)}
                className="flex-shrink-0 w-[270px] sm:w-[320px] md:w-[340px] lg:w-[calc(25%-15px)] min-h-[250px] sm:min-h-[290px] lg:min-h-[320px] snap-start relative overflow-hidden group cursor-pointer border border-[#193B2F]/15 shadow-lg select-none bg-black/20"
              >
                {/* THUMBNAIL IMAGE */}
                <SmoothImage
                  src={item.thumb || "/images/phoi_canh_1.png"}
                  alt={item.title}
                  fill
                  sizes="(max-width: 768px) 320px, (max-width: 1024px) 340px, 25vw"
                  priority
                  className="object-cover object-center transition-transform duration-700 group-hover:scale-105"
                />

                {/* DARK GRADIENT OVERLAY */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent z-10 pointer-events-none" />

                {/* VIDEO PLAY BUTTON BADGE */}
                {item.type === "video" && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
                    <div className="w-11 h-11 rounded-full border border-white/50 bg-black/40 text-white flex items-center justify-center backdrop-blur-sm group-hover:scale-110 transition-transform shadow-lg">
                      <svg
                        className="w-4 h-4 fill-current ml-0.5"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                )}

                {/* BOTTOM OVERLAY TEXT */}
                <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 z-20 flex flex-col justify-end text-white pointer-events-none">
                  {item.tag && (
                    <span className="text-[9.5px] sm:text-[10px] font-sans font-bold tracking-widest text-white/75 uppercase mb-1">
                      {item.tag}
                    </span>
                  )}
                  {item.title && (
                    <h3 className="font-lora text-base sm:text-lg font-normal text-white leading-snug line-clamp-2">
                      {renderTextWithBreaks(item.title)}
                    </h3>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* REUSABLE GALLERY MODAL (FOR IMAGES) */}
      <GalleryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        media={modalMedia}
        initialIndex={modalIndex}
      />

      {/* DEDICATED VIDEO MODAL (STRICTLY FOR VIDEO PLAYBACK) */}
      <VideoModal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        videoUrl={videoUrl}
        title={videoTitle}
      />
    </section>
  );
}
